import React from "react";
import {
	Bell,
	Building2,
	Clock,
	CreditCard,
	Filter,
	Handshake,
	LucideIcon,
	Plug,
	Tag,
	Terminal,
} from "lucide-react";
import { NavTreeNode } from "@/components/Core/AppNavTree";
import Organisation from "@/models/Organisation";
import NotificationSettingsTab from "./NotificationSettingsTab";
import AnpassadeFaltPage from "./leaves/AnpassadeFaltPage";
import AnstallningsavtalPage from "./leaves/AnstallningsavtalPage";
import ArbetsgivarintygPage from "./leaves/ArbetsgivarintygPage";
import BasuppgifterPage from "./leaves/BasuppgifterPage";
import BetaFunktionalitetPage from "./leaves/BetaFunktionalitetPage";
import CertifikatPage from "./leaves/CertifikatPage";
import FaktureringPage from "./leaves/FaktureringPage";
import FortnoxFaktureringPage from "./leaves/FortnoxFaktureringPage";
import FortnoxLonPage from "./leaves/FortnoxLonPage";
import KompetensTreePage from "./leaves/KompetensTreePage";
import KontaktuppgifterPage from "./leaves/KontaktuppgifterPage";
import KonsultfilterPage from "./leaves/KonsultfilterPage";
import KonsulttaggarPage from "./leaves/KonsulttaggarPage";
import MatchningsreglerPage from "./leaves/MatchningsreglerPage";
import KundfilterPage from "./leaves/KundfilterPage";
import KundtaggarPage from "./leaves/KundtaggarPage";
import LonPage from "./leaves/LonPage";
import LonekoderPage from "./leaves/LonekoderPage";
import NekningsanledningarPage from "./leaves/NekningsanledningarPage";
import PersonalfilPage from "./leaves/personFile/PersonalfilPage";
import PoolerPage from "./leaves/PoolerPage";
import ScriptPage from "./leaves/ScriptPage";
import SakerhetPage from "./leaves/SakerhetPage";
import StandardavtalPage from "./leaves/StandardavtalPage";
import DotterbolagPage from "./leaves/DotterbolagPage";
import TidrapporteringPage from "./leaves/TidrapporteringPage";
import UppdragsfilterPage from "./leaves/UppdragsfilterPage";
import UppdragstaggarPage from "./leaves/UppdragstaggarPage";

/**
 * Single source of truth for the settings navigation. Each leaf `id` doubles as
 * its route segment under `/settings`. Group nodes are pure containers (they
 * only expand/collapse, never routed) and may nest other groups; leaf nodes are
 * routed directly. Both the AppNavTree (`settingsTree()`) and the route table
 * (`settingsRoutes()`) are derived from `NODES` so they can't drift apart —
 * including the org gating, which is applied once in `visibleNodes()`.
 */
type SettingsNode =
	| { kind: "group"; id: string; label: string; icon?: LucideIcon; children: SettingsNode[] }
	| {
			kind: "leaf";
			id: string;
			label: string;
			icon?: LucideIcon;
			element: React.ReactElement;
			/**
			 * Org gate. A leaf without a predicate is always shown; one whose
			 * predicate returns false appears neither in the nav nor in the routes
			 * (a direct URL then falls through to `SettingsLanding`).
			 */
			visible?: (organisation: Organisation) => boolean;
	  };

const NODES: SettingsNode[] = [
	{
		kind: "group",
		id: "organisation",
		label: "Organisation",
		icon: Building2,
		children: [
			{ kind: "leaf", id: "basuppgifter", label: "Basuppgifter", element: <BasuppgifterPage /> },
			{
				kind: "leaf",
				id: "kontaktuppgifter",
				label: "Kontaktuppgifter",
				element: <KontaktuppgifterPage />,
			},
			{ kind: "leaf", id: "standardavtal", label: "Standardavtal", element: <StandardavtalPage /> },
			{
				kind: "leaf",
				id: "anstallningsavtal",
				label: "Anställningsavtal",
				element: <AnstallningsavtalPage />,
			},
			{ kind: "leaf", id: "subsidiaries", label: "Dotterbolag", element: <DotterbolagPage /> },
			{ kind: "leaf", id: "pooler", label: "Pooler", element: <PoolerPage /> },
			{
				kind: "leaf",
				id: "anpassade-falt",
				label: "Anpassade fält",
				element: <AnpassadeFaltPage />,
			},
			{ kind: "leaf", id: "sakerhet", label: "Säkerhet", element: <SakerhetPage /> },
		],
	},
	{
		kind: "group",
		id: "bokning-matchning",
		label: "Bokning & matchning",
		icon: Handshake,
		children: [
			{
				kind: "leaf",
				id: "tjanster",
				label: "Tjänster & kompetenser",
				element: <KompetensTreePage />,
			},
			{
				kind: "leaf",
				id: "matchningsregler",
				label: "Matchningsregler",
				element: <MatchningsreglerPage />,
			},
			{
				kind: "leaf",
				id: "neka-uppdrag",
				label: "Neka uppdrag",
				element: <NekningsanledningarPage />,
			},
		],
	},
	{
		kind: "group",
		id: "listfilter",
		label: "Filtrering",
		icon: Filter,
		children: [
			{ kind: "leaf", id: "konsultfilter", label: "Konsultfilter", element: <KonsultfilterPage /> },
			{ kind: "leaf", id: "kundfilter", label: "Kundfilter", element: <KundfilterPage /> },
			{
				kind: "leaf",
				id: "uppdragsfilter",
				label: "Uppdragsfilter",
				element: <UppdragsfilterPage />,
			},
		],
	},
	{
		kind: "group",
		id: "taggar",
		label: "Taggar & certifikat",
		icon: Tag,
		children: [
			{
				kind: "leaf",
				id: "konsulttaggar",
				label: "Konsulttaggar",
				element: <KonsulttaggarPage />,
			},
			{ kind: "leaf", id: "kundtaggar", label: "Kundtaggar", element: <KundtaggarPage /> },
			{
				kind: "leaf",
				id: "uppdragstaggar",
				label: "Uppdragstaggar",
				element: <UppdragstaggarPage />,
			},
			{ kind: "leaf", id: "certifikat", label: "Certifikat", element: <CertifikatPage /> },
		],
	},
	{
		kind: "group",
		id: "ekonomi",
		label: "Ekonomi",
		icon: CreditCard,
		children: [
			{ kind: "leaf", id: "fakturering", label: "Fakturering", element: <FaktureringPage /> },
			{ kind: "leaf", id: "lon", label: "Lön", element: <LonPage /> },
			{ kind: "leaf", id: "lonekoder", label: "Lönearter & koder", element: <LonekoderPage /> },
			{
				kind: "leaf",
				id: "personalfil",
				label: "Personalfil",
				element: <PersonalfilPage />,
				// Only organisations exporting salary to Hogia have a person file.
				visible: (org) => org.salarySettings?.exportType === "HOGIA",
			},
		],
	},
	{
		kind: "leaf",
		id: "tidrapportering",
		label: "Tidrapportering",
		icon: Clock,
		element: <TidrapporteringPage />,
	},
	{
		kind: "leaf",
		id: "notifications",
		label: "Notifieringar",
		icon: Bell,
		element: <NotificationSettingsTab />,
	},
	{
		kind: "group",
		id: "integrationer",
		label: "Integrationer",
		icon: Plug,
		children: [
			{
				kind: "leaf",
				id: "fortnox-fakturering",
				label: "Fortnox fakturering",
				element: <FortnoxFaktureringPage />,
			},
			{ kind: "leaf", id: "fortnox-lon", label: "Fortnox lön", element: <FortnoxLonPage /> },
			{
				kind: "leaf",
				id: "arbetsgivarintyg",
				label: "Arbetsgivarintyg.nu",
				element: <ArbetsgivarintygPage />,
			},
		],
	},
	{
		kind: "group",
		id: "avancerat",
		label: "Avancerat",
		icon: Terminal,
		children: [
			{ kind: "leaf", id: "script", label: "Script", element: <ScriptPage /> },
			{
				kind: "leaf",
				id: "beta",
				label: "Beta-funktionalitet",
				element: <BetaFunktionalitetPage />,
			},
		],
	},
];

/** Tree model handed to `AppNavTree` (no React elements — labels/ids/icons only). */
const toTreeNode = (node: SettingsNode): NavTreeNode =>
	node.kind === "group"
		? { id: node.id, label: node.label, icon: node.icon, children: node.children.map(toTreeNode) }
		: { id: node.id, label: node.label, icon: node.icon };

/**
 * The nodes this organisation may see: gated leaves are dropped, and so are
 * groups left empty by that. Applied before both derivations, so a hidden leaf
 * is missing from the nav *and* from the routes. Without an organisation (still
 * loading) every gated leaf is hidden — it appears once the org resolves.
 */
const visibleNodes = (nodes: SettingsNode[], organisation?: Organisation): SettingsNode[] =>
	nodes.flatMap<SettingsNode>((node) => {
		if (node.kind === "leaf") {
			return !node.visible || (organisation && node.visible(organisation)) ? [node] : [];
		}
		const children = visibleNodes(node.children, organisation);
		return children.length ? [{ ...node, children }] : [];
	});

export interface SettingsRoute {
	path: string;
	element: React.ReactElement;
}

/** Flat route table: one entry per routable leaf, at any depth. */
const collectRoutes = (nodes: SettingsNode[]): SettingsRoute[] =>
	nodes.flatMap((n) =>
		n.kind === "group" ? collectRoutes(n.children) : [{ path: n.id, element: n.element }]
	);

export const settingsTree = (organisation?: Organisation): NavTreeNode[] =>
	visibleNodes(NODES, organisation).map(toTreeNode);

export const settingsRoutes = (organisation?: Organisation): SettingsRoute[] =>
	collectRoutes(visibleNodes(NODES, organisation));
